import { useState, useCallback, useEffect } from "react";
import type {
	EvaluationPanelMember,
	AddPanelMemberPayload,
	SendInvitationsPayload,
	PanelMode,
} from "../types/panel.type";
import {
	getPanelMembers,
	addPanelMember as addPanelMemberApi,
	removePanelMember as removePanelMemberApi,
	sendPanelInvitations as sendPanelInvitationsApi,
	resendPanelInvitations as resendPanelInvitationsApi,
} from "../api/panel-api";

interface UseEvaluationPanelOptions {
	initialMode?: PanelMode;
	rfxId: number;
}

interface UseEvaluationPanelReturn {
	error: string | null;
	hasInvitedMembers: boolean;
	hasPendingMembers: boolean;
	isLoading: boolean;
	panelMembers: EvaluationPanelMember[];
	panelMode: PanelMode;
	addMember: (payload: AddPanelMemberPayload) => Promise<void>;
	fetchPanel: () => Promise<void>;
	removeMember: (memberId: number) => Promise<void>;
	resendInvitations: (memberIds: number[]) => Promise<{ sent: number }>;
	sendInvitations: (payload: SendInvitationsPayload) => Promise<{ sent: number }>;
	setMode: (mode: PanelMode) => void;
}

export const useEvaluationPanel = ({
	rfxId,
	initialMode,
}: UseEvaluationPanelOptions): UseEvaluationPanelReturn => {
	const [panelMembers, setPanelMembers] = useState<EvaluationPanelMember[]>([]);
	const [panelMode, setPanelMode] = useState<PanelMode>(initialMode ?? "manual_solo");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const hasInvitedMembers = panelMembers.some((m) => m.status === "invited");
	const hasPendingMembers = panelMembers.some((m) => m.status === "pending");

	const fetchPanel = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const members = await getPanelMembers(rfxId);
			setPanelMembers(members);
		} catch (err) {
			setError("Failed to load panel members");
		} finally {
			setIsLoading(false);
		}
	}, [rfxId]);

	const addMember = useCallback(
		async (payload: AddPanelMemberPayload) => {
			setError(null);
			try {
				await addPanelMemberApi(rfxId, payload);
				await fetchPanel();
			} catch (err: unknown) {
				const axiosErr = err as { response?: { data?: { errors?: Record<string, string[]>; message?: string }; status?: number; } };
				if (axiosErr?.response?.status === 422) {
					const errors = axiosErr.response.data?.errors;
					const firstError = errors ? Object.values(errors).flat()[0] : axiosErr.response.data?.message;
					setError(firstError ?? "Validation failed");
				} else {
					setError("Failed to add panel member");
				}
				throw err;
			}
		},
		[rfxId, fetchPanel]
	);

	const removeMember = useCallback(
		async (memberId: number) => {
			setError(null);
			try {
				await removePanelMemberApi(rfxId, memberId);
				await fetchPanel();
			} catch (err) {
				setError("Failed to remove panel member");
				throw err;
			}
		},
		[rfxId, fetchPanel]
	);

	const sendInvitations = useCallback(
		async (payload: SendInvitationsPayload) => {
			setError(null);
			try {
				const result = await sendPanelInvitationsApi(rfxId, payload);
				await fetchPanel();
				return result;
			} catch (err) {
				setError("Failed to send invitations");
				throw err;
			}
		},
		[rfxId, fetchPanel]
	);

	const resendInvitations = useCallback(
		async (memberIds: number[]) => {
			setError(null);
			try {
				const result = await resendPanelInvitationsApi(rfxId, memberIds);
				await fetchPanel();
				return result;
			} catch (err) {
				setError("Failed to resend invitations");
				throw err;
			}
		},
		[rfxId, fetchPanel]
	);

	const setMode = useCallback((mode: PanelMode) => {
		setPanelMode(mode);
	}, []);

	useEffect(() => {
		fetchPanel();
	}, [fetchPanel]);

	return {
		panelMembers,
		panelMode,
		isLoading,
		error,
		hasInvitedMembers,
		hasPendingMembers,
		fetchPanel,
		addMember,
		removeMember,
		sendInvitations,
		resendInvitations,
		setMode,
	};
};
